import { TableFiltersBar } from '../TableFiltersBar';
import { usersFiltersConfig } from '../../utils/tableFilters';
import { formatUserFullName, getEditUserPath, getUserStatusLabel } from '../../store';
import { COLORS, DATE_FORMAT_DEFAULT, OrderTypes } from '@/constants';
import { getFullDateFromISO } from '@/utils';
import {
  Box,
  Button,
  ButtonIcon,
  Dropdown,
  ICONS_NAME,
  Icon,
  Table,
} from '@servicepipe/ui-components';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';

const AccessGroupsCell = ({ groups }) => {
  const names = groups?.map((group) => group.name?.trim()).filter(Boolean) ?? [];
  const joined = names.join(', ');

  if (!names.length) {
    return <>—</>;
  }

  return (
    <Tooltip
      title={
        <Box direction="column">
          {names.map((name, index) => (
            <span key={`${name}-${index}`}>{name}</span>
          ))}
        </Box>
      }
    >
      <span
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {joined}
      </span>
    </Tooltip>
  );
};

export const UsersTable = ({
  data,
  onPageChange,
  queryParams,
  totalPages,
  totalCount,
  onSortingChange,
  onFiltersChange,
  loading,
  setDeletedMember,
  handleGetLinkByMember,
}) => {
  const navigate = useNavigate();
  const { id: partnerClientAccountId } = useParams();

  const sortingInitial = {
    id: queryParams.sortKey || '',
    desc: queryParams.sortDirection === OrderTypes.Desc,
  };

  const handleActionClick = (memberId, action, email) => {
    if (action === 'delete') {
      setDeletedMember({ id: memberId, email, deleted: true });
      return;
    }

    navigate(getEditUserPath(partnerClientAccountId, memberId));
  };

  const columns = [
    {
      width: 110,
      minWidth: 110,
      enableSorting: true,
      header: 'ID',
      id: 'id',
      body: 'id',
    },
    {
      header: 'Email',
      id: 'email',
      minWidth: 220,
      width: 220,
      body: 'email',
    },
    {
      header: 'Name',
      id: 'name',
      minWidth: 160,
      width: 160,
      body: ({ row }) => formatUserFullName(row?.original?.name) || '-',
    },
    {
      header: 'Access groups',
      id: 'groups',
      minWidth: 220,
      width: 220,
      body: ({ row }) => <AccessGroupsCell groups={row?.original?.groups} />,
    },
    {
      header: 'Administrator',
      id: 'isInDefaultGroup',
      minWidth: 120,
      width: 120,
      meta: { justify: 'center' },
      body: ({ row }) =>
        row?.original?.isInDefaultGroup ? (
          <Icon name={ICONS_NAME.check2} width={18} height={18} color={COLORS.blackHighEmphasis} />
        ) : (
          '—'
        ),
    },
    {
      header: 'Created At',
      id: 'createdAt',
      minWidth: 140,
      width: 140,
      body: ({ getValue }) =>
        getValue() ? getFullDateFromISO(getValue(), DATE_FORMAT_DEFAULT) : '-',
    },
    {
      enableSorting: true,
      header: 'Status',
      id: 'status',
      minWidth: 120,
      width: 120,
      meta: { justify: 'center' },
      body: ({ row }) => getUserStatusLabel(row?.original?.status, row?.original?.deletedAt),
    },
    {
      header: 'Authentication type',
      id: 'authenticationType',
      minWidth: 180,
      width: 180,
      body: 'authenticationType',
    },
    {
      header: '',
      id: 'showLink',
      minWidth: 120,
      width: 120,
      meta: { justify: 'center' },
      body: ({ row }) => (
        <Button size="small" variant="solid" onClick={() => handleGetLinkByMember(row.original.id)}>
          Show link
        </Button>
      ),
    },
    {
      id: 'actions',
      width: 50,
      minWidth: 50,
      meta: { justify: 'center', variant: 'action' },
      header: '',
      body: ({ row }) => (
        <Dropdown
          onClick={({ value }) =>
            handleActionClick(Number(row.original.id), String(value), row.original.email)
          }
          options={[
            { value: 'edit', label: 'General' },
            {
              color: COLORS.errorDark,
              value: 'delete',
              label: 'Delete',
              disabled: Boolean(row.original.deletedAt) || row.original.status === 'DELETED',
            },
          ]}
        >
          <ButtonIcon size="small" variant="ghost" icon={ICONS_NAME.moreHoriz} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Box direction="column" width="100%" minWidth="0">
      <TableFiltersBar
        filtersConfig={usersFiltersConfig}
        queryFilters={queryParams.filters ?? []}
        onFiltersChange={onFiltersChange}
      />

      <Table
        loading={loading}
        data={data}
        columns={columns}
        page={queryParams.page}
        size={queryParams.pageSize}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onSortingChange={onSortingChange}
        initialStateSort={sortingInitial}
        isFixedLayout
        noDataText="No users found. Try different filter values."
      />
    </Box>
  );
};
